﻿package pl.zar.tikthrow.ui.screen

import androidx.compose.foundation.background
import androidx.compose.foundation.border
import androidx.compose.foundation.clickable
import androidx.compose.foundation.layout.*
import androidx.compose.foundation.lazy.LazyRow
import androidx.compose.foundation.lazy.items
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.foundation.verticalScroll
import androidx.compose.material.icons.Icons
import androidx.compose.foundation.layout.navigationBarsPadding
import androidx.compose.foundation.layout.statusBarsPadding
import androidx.compose.material.icons.filled.ArrowBack
import androidx.compose.material.icons.filled.Add
import androidx.compose.foundation.BorderStroke
import androidx.compose.material3.*
import androidx.compose.runtime.*
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.graphics.Brush
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.text.TextStyle
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import pl.zar.tikthrow.shared.domain.Platform
import pl.zar.tikthrow.ui.media.ImageFromUri
import pl.zar.tikthrow.ui.media.MediaItem
import pl.zar.tikthrow.ui.media.rememberMediaPickerState
import pl.zar.tikthrow.ui.theme.*
import pl.zar.tikthrow.viewmodel.SavedPostsViewModel
import kotlin.math.roundToInt

private val platformColors = mapOf(
    Platform.TWITTER   to Color(0xFF1D9BF0),
    Platform.INSTAGRAM to Color(0xFFE1306C),
    Platform.FACEBOOK  to Color(0xFF1877F2),
    Platform.TIKTOK    to Color(0xFFFF0050),
    Platform.YOUTUBE   to Color(0xFFFF0000),
    Platform.DISCORD   to Color(0xFF5865F2),
)

private val gradientBrush = Brush.linearGradient(listOf(GradientStart, GradientEnd))

@Composable
fun AddPostScreen(
    viewModel: SavedPostsViewModel,
    onBack: () -> Unit,
    editPostId: String? = null
) {
    val existing = remember(editPostId) { editPostId?.let { viewModel.getById(it) } }
    var media by remember { mutableStateOf(existing?.media ?: emptyList()) }
    var caption by remember { mutableStateOf(existing?.caption ?: "") }
    var selectedPlatforms by remember { mutableStateOf(existing?.platforms ?: emptySet()) }

    val picker = rememberMediaPickerState { newMedia -> media = media + newMedia }

    val isEditing = existing != null
    val canSave = media.isNotEmpty() || caption.isNotBlank()

    Surface(modifier = Modifier.fillMaxSize(), color = MaterialTheme.colorScheme.background) {
        Column(modifier = Modifier.fillMaxSize()) {
            TopBar(onBack = onBack, isEditing = isEditing)

            Box(modifier = Modifier.weight(1f).fillMaxWidth(), contentAlignment = Alignment.TopCenter) {
                Column(
                    modifier = Modifier
                        .widthIn(max = 480.dp)
                        .verticalScroll(rememberScrollState())
                        .padding(16.dp),
                    verticalArrangement = Arrangement.spacedBy(24.dp)
                ) {
                    MediaSection(media = media, onAddMore = { picker.launch() })
                    CaptionSection(
                        caption = caption,
                        onCaptionChange = { caption = it },
                        onGenerate = { caption = "✨ Tu pojawi się wygenerowany opis na podstawie Twoich mediów i wybranych platform." }
                    )
                    PlatformSection(
                        selected = selectedPlatforms,
                        onToggle = { platform ->
                            selectedPlatforms = if (platform in selectedPlatforms)
                                selectedPlatforms - platform
                            else
                                selectedPlatforms + platform
                        }
                    )
                    Spacer(Modifier.height(8.dp))
                }
            }

            BottomBar(isEditing = isEditing, enabled = canSave, onSave = {
                if (isEditing && existing != null)
                    viewModel.update(existing.id, media, caption, selectedPlatforms)
                else
                    viewModel.save(media, caption, selectedPlatforms)
                onBack()
            })
        }
    }
}

@Composable
private fun TopBar(onBack: () -> Unit, isEditing: Boolean) {
    Surface(
        color = Slate100,
        shadowElevation = 2.dp
    ) {
        Row(
            modifier = Modifier
                .fillMaxWidth()
                .statusBarsPadding()
                .padding(horizontal = 8.dp, vertical = 12.dp),
            verticalAlignment = Alignment.CenterVertically,
            horizontalArrangement = Arrangement.spacedBy(4.dp)
        ) {
            IconButton(onClick = onBack) {
                Icon(Icons.Default.ArrowBack, contentDescription = "Wróć", tint = Slate800)
            }
            Text(
                text = if (isEditing) "Edytuj post" else "Na zapas",
                style = TextStyle(
                    brush = gradientBrush,
                    fontSize = 20.sp,
                    fontWeight = FontWeight.ExtraBold,
                )
            )
        }
    }
}

@Composable
private fun MediaSection(media: List<MediaItem>, onAddMore: () -> Unit) {
    Column(verticalArrangement = Arrangement.spacedBy(12.dp)) {
        Text(
            text = "MEDIA",
            fontSize = 11.sp,
            fontWeight = FontWeight.ExtraBold,
            letterSpacing = 1.5.sp,
            color = Slate400
        )

        if (media.isEmpty()) {
            Box(
                modifier = Modifier
                    .fillMaxWidth()
                    .height(180.dp)
                    .clip(RoundedCornerShape(6.dp))
                    .background(Slate100)
                    .border(
                        width = 2.dp,
                        color = Slate300,
                        shape = RoundedCornerShape(6.dp)
                    )
                    .clickable { onAddMore() },
                contentAlignment = Alignment.Center
            ) {
                Column(
                    horizontalAlignment = Alignment.CenterHorizontally,
                    verticalArrangement = Arrangement.spacedBy(10.dp)
                ) {
                    Box(
                        modifier = Modifier
                            .size(48.dp)
                            .clip(RoundedCornerShape(4.dp))
                            .background(gradientBrush),
                        contentAlignment = Alignment.Center
                    ) {
                        Icon(
                            Icons.Default.Add,
                            contentDescription = null,
                            modifier = Modifier.size(24.dp),
                            tint = Color.White
                        )
                    }
                    Text(
                        "Dodaj zdjęcia lub wideo",
                        fontSize = 14.sp,
                        fontWeight = FontWeight.SemiBold,
                        color = Slate500
                    )
                }
            }
        } else {
            LazyRow(horizontalArrangement = Arrangement.spacedBy(8.dp)) {
                items(media) { item ->
                    val aspectRatio = if (item.isVideo) 9f / 16f else 1f
                    Box(
                        modifier = Modifier
                            .height(100.dp)
                            .aspectRatio(aspectRatio)
                            .clip(RoundedCornerShape(4.dp))
                            .background(Slate100)
                    ) {
                        ImageFromUri(
                            uri = item.uri,
                            isVideo = item.isVideo,
                            modifier = Modifier.matchParentSize()
                        )
                        if (item.isVideo && item.fileSizeBytes != null && item.fileSizeBytes > 0L) {
                            Box(
                                modifier = Modifier
                                    .align(Alignment.BottomCenter)
                                    .fillMaxWidth()
                                    .background(Color(0x99000000))
                                    .padding(vertical = 2.dp),
                                contentAlignment = Alignment.Center
                            ) {
                                Text(
                                    text = formatFileSize(item.fileSizeBytes),
                                    fontSize = 9.sp,
                                    fontWeight = FontWeight.SemiBold,
                                    color = Color.White
                                )
                            }
                        }
                    }
                }
                item {
                    Box(
                        modifier = Modifier
                            .size(100.dp)
                            .clip(RoundedCornerShape(4.dp))
                            .background(Slate100)
                            .border(
                                width = 1.5.dp,
                                color = Slate300,
                                shape = RoundedCornerShape(4.dp)
                            )
                            .clickable { onAddMore() },
                        contentAlignment = Alignment.Center
                    ) {
                        Icon(Icons.Default.Add, contentDescription = "Dodaj więcej", tint = Slate600)
                    }
                }
            }
            val totalSize = media.sumOf { it.fileSizeBytes ?: 0L }
            val sizeText = if (totalSize > 0L) " · ${formatFileSize(totalSize)}" else ""
            Text(
                text = "${media.size} plik${if (media.size == 1) "" else "i/ów"} wybranych$sizeText",
                fontSize = 12.sp,
                color = Slate500
            )
        }
    }
}

@Composable
private fun CaptionSection(caption: String, onCaptionChange: (String) -> Unit, onGenerate: () -> Unit) {
    Column(verticalArrangement = Arrangement.spacedBy(10.dp)) {
        Row(
            modifier = Modifier.fillMaxWidth(),
            verticalAlignment = Alignment.CenterVertically,
            horizontalArrangement = Arrangement.SpaceBetween
        ) {
            Text(
                text = "OPIS",
                fontSize = 11.sp,
                fontWeight = FontWeight.ExtraBold,
                letterSpacing = 1.5.sp,
                color = Slate400
            )
            Surface(
                onClick = onGenerate,
                shape = RoundedCornerShape(8.dp),
                color = Color.Transparent
            ) {
                Box(
                    modifier = Modifier
                        .background(Slate200, RoundedCornerShape(8.dp))
                        .padding(horizontal = 12.dp, vertical = 6.dp)
                ) {
                    Text(
                        text = "✨ Generuj opis",
                        fontSize = 12.sp,
                        fontWeight = FontWeight.Bold,
                        color = Slate800
                    )
                }
            }
        }
        OutlinedTextField(
            value = caption,
            onValueChange = onCaptionChange,
            placeholder = { Text("Co chcesz powiedzieć?", color = Slate400) },
            modifier = Modifier
                .fillMaxWidth()
                .heightIn(min = 110.dp),
            maxLines = 6,
            colors = OutlinedTextFieldDefaults.colors(
                focusedBorderColor = Slate600,
                unfocusedBorderColor = Slate200,
                focusedTextColor = Slate900,
                unfocusedTextColor = Slate900,
                cursorColor = Slate800,
                focusedContainerColor = Slate100,
                unfocusedContainerColor = Slate100,
            ),
            shape = RoundedCornerShape(6.dp)
        )
    }
}

@Composable
private fun PlatformSection(selected: Set<Platform>, onToggle: (Platform) -> Unit) {
    Column(verticalArrangement = Arrangement.spacedBy(12.dp)) {
        Text(
            text = "PUBLIKUJ NA",
            fontSize = 11.sp,
            fontWeight = FontWeight.ExtraBold,
            letterSpacing = 1.5.sp,
            color = Slate400
        )
        Column(verticalArrangement = Arrangement.spacedBy(8.dp)) {
            Platform.entries.forEach { platform ->
                val isSelected = platform in selected
                val color = platformColors[platform] ?: Slate600
                Surface(
                    onClick = { onToggle(platform) },
                    shape = RoundedCornerShape(6.dp),
                    color = if (isSelected) Slate200 else Slate100,
                    shadowElevation = if (isSelected) 0.dp else 1.dp,
                    border = if (isSelected) BorderStroke(1.5.dp, Slate600) else BorderStroke(1.dp, Slate200)
                ) {
                    Row(
                        modifier = Modifier
                            .fillMaxWidth()
                            .padding(horizontal = 16.dp, vertical = 14.dp),
                        verticalAlignment = Alignment.CenterVertically,
                        horizontalArrangement = Arrangement.SpaceBetween
                    ) {
                        Row(
                            verticalAlignment = Alignment.CenterVertically,
                            horizontalArrangement = Arrangement.spacedBy(12.dp)
                        ) {
                            Box(
                                modifier = Modifier
                                    .size(10.dp)
                                    .clip(RoundedCornerShape(2.dp))
                                    .background(color)
                            )
                            Text(
                                text = platform.displayName,
                                fontSize = 15.sp,
                                fontWeight = if (isSelected) FontWeight.Bold else FontWeight.Medium,
                                color = if (isSelected) Slate900 else Slate800
                            )
                        }
                        Checkbox(
                            checked = isSelected,
                            onCheckedChange = { onToggle(platform) },
                            colors = CheckboxDefaults.colors(
                                checkedColor = Slate800,
                                uncheckedColor = Slate300,
                                checkmarkColor = Slate50
                            )
                        )
                    }
                }
            }
        }
    }
}

@Composable
private fun BottomBar(isEditing: Boolean, enabled: Boolean, onSave: () -> Unit) {
    Surface(
        color = Slate100,
        shadowElevation = 8.dp
    ) {
        Box(
            modifier = Modifier
                .fillMaxWidth()
                .navigationBarsPadding()
                .padding(horizontal = 16.dp, vertical = 14.dp)
                .height(52.dp)
                .clip(RoundedCornerShape(6.dp))
                .background(
                    if (enabled) gradientBrush
                    else Brush.linearGradient(listOf(Slate200, Slate200))
                )
                .clickable(enabled = enabled, onClick = onSave),
            contentAlignment = Alignment.Center
        ) {
            Text(
                text = if (isEditing) "Zapisz zmiany" else "Zapisz na zapas",
                color = if (enabled) Color.White else Slate400,
                fontSize = 16.sp,
                fontWeight = FontWeight.ExtraBold,
                letterSpacing = (-0.3).sp
            )
        }
    }
}

private fun formatFileSize(bytes: Long): String {
    return when {
        bytes >= 1024L * 1024L * 1024L -> {
            val gb = bytes / (1024.0 * 1024.0 * 1024.0)
            "${formatOneDecimal(gb)} GB"
        }
        bytes >= 1024L * 1024L -> {
            val mb = bytes / (1024.0 * 1024.0)
            if (mb >= 100) "${mb.roundToInt()} MB" else "${formatOneDecimal(mb)} MB"
        }
        bytes >= 1024L -> {
            val kb = bytes / 1024.0
            "${kb.roundToInt()} KB"
        }
        else -> "$bytes B"
    }
}

private fun formatOneDecimal(value: Double): String {
    val tenths = (value * 10).roundToInt()
    return "${tenths / 10}.${tenths % 10}"
}
